<template>
  <div class="overview-container">
    <div class="page-head">
      <div>
        <h3>Platform Insights</h3>
        <p class="page-head-sub">Sales by channel and month</p>
      </div>
    </div>

    <!-- Pie Chart Section -->
    <div class="chart-section">
      <h3 class="section-title">{{ moment().format('MMMM') }} Sales Distribution</h3>
      <div class="chart-wrapper">
        <pie-chart :data="pie"></pie-chart>
      </div>
    </div>

    <!-- Year Selector Tabs -->
    <div class="year-tabs">
      <button
        :class="['year-tab', selectedYear === 'current' ? 'active' : '']"
        @click="selectedYear = 'current'">
        {{ curYear }}
      </button>
      <button
        :class="['year-tab', selectedYear === 'previous' ? 'active' : '']"
        @click="selectedYear = 'previous'">
        {{ prevYear }}
      </button>
    </div>

    <!-- Current Year Data -->
    <div v-show="selectedYear === 'current'" class="data-section">
      <!-- Mobile View: Accordion -->
      <div class="d-md-none mobile-accordion">
        <div
          v-for="(t, i) of rawDataCur"
          :key="'mobile-cur-' + t.name"
          class="accordion-item">
          <div
            class="accordion-header"
            :class="[getHeaderClass(t.name, i), expandedCategories.includes(`cur-${i}`) ? 'expanded' : '']"
            @click="toggleCategory(`cur-${i}`)">
            <div class="header-content">
              <span class="category-name">{{ t.name.toUpperCase() }}</span>
              <span class="category-total">
                {{ getCategoryTotal(t.data) }} NOK
              </span>
            </div>
            <b-icon
              :icon="expandedCategories.includes(`cur-${i}`) ? 'chevron-up' : 'chevron-down'"
              class="expand-icon"></b-icon>
          </div>
          <div v-show="expandedCategories.includes(`cur-${i}`)" class="accordion-body">
            <div
              v-for="line of t.data"
              :key="line.month"
              class="month-row">
              <span class="month-label">{{ monthMap[line.month] }}</span>
              <span class="month-value">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop View: Tables -->
      <div class="d-none d-md-block desktop-tables">
        <div class="tables-grid">
          <div
            v-for="(t, i) of rawDataCur"
            :key="'desktop-cur-' + t.name"
            class="table-card">
            <div class="table-header" :class="getHeaderClass(t.name, i)">
              {{ t.name.toUpperCase() }}
            </div>
            <table class="stats-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th class="text-right">NOK</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line of t.data" :key="line.month">
                  <td>{{ monthMap[line.month] }}</td>
                  <td class="text-right">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td class="text-right">{{ getCategoryTotal(t.data) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Previous Year Data -->
    <div v-show="selectedYear === 'previous'" class="data-section">
      <!-- Mobile View: Accordion -->
      <div class="d-md-none mobile-accordion">
        <div
          v-for="(t, i) of rawDataPrev"
          :key="'mobile-prev-' + t.name"
          class="accordion-item">
          <div
            class="accordion-header"
            :class="[getHeaderClass(t.name, i), expandedCategories.includes(`prev-${i}`) ? 'expanded' : '']"
            @click="toggleCategory(`prev-${i}`)">
            <div class="header-content">
              <span class="category-name">{{ t.name.toUpperCase() }}</span>
              <span class="category-total">
                {{ getCategoryTotal(t.data) }} NOK
              </span>
            </div>
            <b-icon
              :icon="expandedCategories.includes(`prev-${i}`) ? 'chevron-up' : 'chevron-down'"
              class="expand-icon"></b-icon>
          </div>
          <div v-show="expandedCategories.includes(`prev-${i}`)" class="accordion-body">
            <div
              v-for="line of t.data"
              :key="line.month"
              class="month-row">
              <span class="month-label">{{ monthMap[line.month] }}</span>
              <span class="month-value">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop View: Tables -->
      <div class="d-none d-md-block desktop-tables">
        <div class="tables-grid">
          <div
            v-for="(t, i) of rawDataPrev"
            :key="'desktop-prev-' + t.name"
            class="table-card">
            <div class="table-header" :class="getHeaderClass(t.name, i)">
              {{ t.name.toUpperCase() }}
            </div>
            <table class="stats-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th class="text-right">NOK</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line of t.data" :key="line.month">
                  <td>{{ monthMap[line.month] }}</td>
                  <td class="text-right">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td class="text-right">{{ getCategoryTotal(t.data) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios';
import fixed from '@/fixed';
import moment from 'moment';

export default {
  data: () => ({
    curYear: moment().format('YYYY'),
    prevYear: moment().subtract(1, 'years').format('YYYY'),
    chart: [],
    pie: [],
    rawDataCur: [],
    rawDataPrev: [],
    selectedYear: 'current', // For tab switching
    expandedCategories: [], // For mobile accordion
    monthMap: {
      1: 'Jan',
      2: 'Feb',
      3: 'Mar',
      4: 'Apr',
      5: 'May',
      6: 'Jun',
      7: 'Jul',
      8: 'Aug',
      9: 'Sep',
      10: 'Okt',
      11: 'Nov',
      12: 'Dec',
    },
    moment,
  }),
  async mounted() {
    const rwc = await this.getOverview(this.curYear);
    const rwp = await this.getOverview(this.prevYear);

    this.rawDataCur = rwc.sort((a, b) => a.name.localeCompare(b.name));
    this.rawDataPrev = rwp.sort((a, b) => a.name.localeCompare(b.name));

    this.decorateTotals(this.rawDataCur);
    this.decorateTotals(this.rawDataPrev);

    this.pie = this.generatePieFormat(this.rawDataCur);
    // this.chart = this.generateChartFormat(this.rawDataPrev);
  },
  methods: {
    toggleCategory(categoryId) {
      const index = this.expandedCategories.indexOf(categoryId);
      if (index > -1) {
        this.expandedCategories.splice(index, 1);
      } else {
        this.expandedCategories.push(categoryId);
      }
    },
    getCategoryTotal(data) {
      const total = data.reduce((sum, line) => sum + line.totalSales, 0);
      return total.toLocaleString('en', { maximumFractionDigits: 0 });
    },
    getHeaderClass(name) {
      // Check if it's the "Total" category
      if (name.toLowerCase() === 'total') {
        return 'header-total';
      }
      return 'header-category';
    },
    decorateTotals(dataset) {
      // Calculate total sales per month across all names
      const totalSalesPerMonth = dataset.reduce((acc, { data }) => {
        data.forEach(({ month, totalSales }) => {
          acc[month] = (acc[month] || 0) + totalSales;
        });
        return acc;
      }, {});

      // Convert the aggregated totals into the desired format
      const totalData = Object.entries(totalSalesPerMonth).map(([month, totalSales]) => ({
        month: parseInt(month, 10),
        totalSales,
      }));

      // Sort by month
      totalData.sort((a, b) => a.month - b.month);

      // Add the total entry to the dataset
      dataset.push({ name: 'Total', data: totalData });
    },
    generateChartFormat(data) {
      const chartData = [];
      data.forEach((source) => {
        const record = {
          name: source.name,
          data: source.data.map((line) => [[this.monthMap[line.month]], line.totalSales]),
        };
        chartData.push(record);
      });
      return chartData;
    },
    generatePieFormat(data) {
      const pieData = [];
      for (let i = 0; i < data.length - 1; i += 1) {
        pieData.push([data[i].name, data[i].data[data[i].data.length - 1].totalSales]);
      }
      return pieData;
    },
    async getOverview(year) {
      try {
        const { data } = await axios.get(
          `${fixed.API_URL}/admin/sales-overview?year=${year}`,
          { withCredentials: true },
        );
        return data;
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.$toast.warning('you\'re not authorised for this action ✋🏻');
        } else {
          this.$toast.info('No data for selected range');
        }
        return null;
      }
    },
  },
};
</script>

<style scoped>
/* Mobile-first Container */
.overview-container {
  padding: 0;
  min-height: 100vh;
  background-color: transparent;
}

/* Chart Section */
.chart-section {
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem 1rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm);
}

.section-title {
  font-size: 1rem;
  font-weight: 650;
  text-align: left;
  margin-bottom: 1rem;
  color: var(--text);
  text-transform: capitalize;
}

.chart-wrapper {
  background-color: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1rem;
}

/* Year Tabs */
.year-tabs {
  display: flex;
  gap: 2px;
  padding: 3px;
  margin: 0 auto 1.5rem; /* centred on phones, left-aligned from md up */
  max-width: 360px;
  background-color: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}

.year-tab {
  flex: 1;
  padding: 0.45rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  background-color: transparent;
  color: var(--text-muted);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.year-tab:hover {
  color: var(--text);
}

.year-tab.active {
  background-color: var(--surface-3);
  color: var(--text);
  box-shadow: var(--shadow-sm);
}

/* Data Section */
.data-section {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile Accordion */
.mobile-accordion {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.accordion-item {
  background-color: var(--surface);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
}

.accordion-header {
  padding: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease-in-out;
  user-select: none;
}

.accordion-header:active {
  transform: scale(0.98);
}

.header-category {
  background-color: var(--surface-3);
}

.header-total {
  background-color: var(--success);
}

.accordion-header.expanded {
  border-bottom: 1px solid var(--border-strong);
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.category-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  letter-spacing: 0.5px;
}

.category-total {
  font-size: 0.875rem;
  color: var(--text-muted);
  font-weight: 500;
}

/* White text for total category on mobile */
.header-total .category-total {
  color: #fff;
}

.expand-icon {
  font-size: 1.25rem;
  color: #fff;
  transition: transform 0.2s ease-in-out;
}

.accordion-header.expanded .expand-icon {
  transform: rotate(180deg);
}

.accordion-body {
  background-color: var(--surface-2);
  animation: slideDown 0.2s ease-in-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 500px;
  }
}

.month-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
}

.month-row:last-child {
  border-bottom: none;
}

.month-label {
  font-weight: 600;
  color: var(--text-muted);
  font-size: 0.875rem;
}

.month-value {
  font-weight: 700;
  color: var(--text);
  font-size: 0.875rem;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

/* Desktop Tables */
.desktop-tables {
  margin-top: 1rem;
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.table-card {
  background-color: var(--surface);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
}

.table-header {
  padding: 1rem;
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--text);
  text-align: center;
  letter-spacing: 0.5px;
}

.table-header.header-category {
  background-color: var(--surface-3);
}

.table-header.header-total {
  background-color: var(--success);
  color: #fff;
}

.stats-table {
  width: 100%;
  margin: 0;
  color: var(--text);
  background-color: var(--surface);
}

.stats-table thead {
  background-color: var(--surface-2);
}

.stats-table th {
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: none;
  color: var(--text-muted);
}

.stats-table tbody tr {
  border-bottom: 1px solid var(--border);
  transition: background-color 0.15s ease-in-out;
}

.stats-table tbody tr:last-child {
  border-bottom: none;
}

.stats-table tbody tr:hover {
  background-color: var(--surface-2);
}

.stats-table td {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  border: none;
  color: var(--text);
}

.stats-table tfoot td {
  font-weight: 700;
  background-color: var(--surface-2);
  border-top: 1px solid var(--border-strong);
}

.stats-table .text-right {
  text-align: right;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* Tablet Responsive (sm breakpoint) */
@media (min-width: 576px) {
  .overview-container {
    padding: 1.5rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .chart-section {
    padding: 2rem;
  }

  .year-tab {
    font-size: 1.25rem;
    padding: 1rem 1.5rem;
  }

  .accordion-header {
    padding: 1.25rem 1.5rem;
  }

  .category-name {
    font-size: 1.125rem;
  }

  .category-total {
    font-size: 1rem;
  }

  .month-row {
    padding: 1rem 1.5rem;
  }

  .month-label,
  .month-value {
    font-size: 1rem;
  }
}

/* Desktop Responsive (md breakpoint) */
@media (min-width: 768px) {
  .overview-container {
    padding: 2rem;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .tables-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  .year-tabs {
    margin-left: 0;
  }
}

/* Large Desktop (lg breakpoint) */
@media (min-width: 992px) {
  .overview-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .tables-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
  }
}

/* Extra Large Desktop (xl breakpoint) */
@media (min-width: 1200px) {
  .tables-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}
</style>
